import { Event } from "event-system";
import { gsap } from "gsap";
import { Container, FederatedPointerEvent, IDestroyOptions, Point, Sprite, Text, TextStyle } from "pixi.js";
import { GameFont } from "../../../common/GameFont";
import { DefaultButton } from "../../../common/components/DefaultButton";
import { IUpgradeBuildingModel } from "../../../common/models/Building/upgrade/IUpgradeBuildingModel";
import { UpgradeResourcePackage } from "../resources/UpgradeResourcePackage";
import { GetRewardContainer } from "./GetRewardContainer";
import { UpgradeCoinButton } from "./UpgradeCoinButton";
import { UpgradeResourceIndicator } from "./UpgradeResource";
import { UpgradeStateType } from "./UpgradeStateType";
import { UpgradeTokenButton } from "./UpgradeTokenButton";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { IFirstTimeExperienceModel } from "../../../common/models/IFirstTimeExperienceModel";

export class UpgradePanel extends Container {
    public static readonly FIX_FRAME_TINT = "#FF7A00";
    public static readonly DONE_FRAME_TINT = "#71FF01";
    public static readonly FIX_TEXT = "NEED TO FIX";
    public static readonly UNLOCK_TEXT = "OPEN AT RANK ";
    public static readonly UPGRADED_TEXT = "UPGRADED";

    public readonly onUpgradeButtonClick: Event<IUpgradeBuildingModel, boolean>;

    private readonly commonResources: CommonResourcesPackage;
    private readonly upgradeResources: UpgradeResourcePackage;

    private upgradeItemModel: IUpgradeBuildingModel;
    private coinsUpgradeStateType: UpgradeStateType;
    private tokensUpgradeStateType: UpgradeStateType;
    private frame: Sprite;
    private fixText: Text;
    private upgradeResourceName: Text;
    private leftButton: DefaultButton;
    private rightButton: DefaultButton;
    private separetor: Sprite;
    private upgradeCoinButton: UpgradeCoinButton;
    private upgradeCoinRewardText: GetRewardContainer;
    private upgradeTokenButton: UpgradeTokenButton;
    private upgradeTokenRewardText: GetRewardContainer;
    private resource: UpgradeResourceIndicator;
    private lockedRibbon: Sprite;
    private lockedText: Text;
    private upgradedText: Text;
    private light: Sprite;
    private tokenButtonAvailable: boolean;

    public get onRightButtonClick(): Event<DefaultButton, FederatedPointerEvent> {
        return this.rightButton.onUp;
    }

    public get onLeftButtonClick(): Event<DefaultButton, FederatedPointerEvent> {
        return this.leftButton.onUp;
    }

    public set blockArrows(value: boolean) {
        this.leftButton.eventMode = value ? "auto" : "static";
        this.rightButton.eventMode = value ? "auto" : "static";
    }

    public get coinButtonPosition(): Point {
        return this.upgradeCoinButton.position;
    }

    public get tokenButtonPosition(): Point {
        return this.upgradeTokenButton.position;
    }

    public constructor(
        commonResources: CommonResourcesPackage,
        upgradeResources: UpgradeResourcePackage,
        selectedUpgradeModel: IUpgradeBuildingModel
    ) {
        super();
        this.commonResources = commonResources;
        this.upgradeResources = upgradeResources;
        this.upgradeItemModel = selectedUpgradeModel;
        this.onUpgradeButtonClick = new Event<IUpgradeBuildingModel, boolean>();
        this.coinsUpgradeStateType = this.getCurrentCoinsUpgradeState();
        this.tokensUpgradeStateType = this.getCurrentTokensUpgradeState();
        this.build();
        this.once("added", this.show.bind(this));
    }

    private getCurrentCoinsUpgradeState(): UpgradeStateType {
        const upgradeElements = this.upgradeItemModel.elements.value;
        return this.upgradeItemModel.lock.value.status ? UpgradeStateType.LOCKED :
            upgradeElements.needToFix > 0 ? UpgradeStateType.FIX :
                upgradeElements.needToUpgrade === 0 ? UpgradeStateType.DONE : UpgradeStateType.AVAILABLE;
    }

    private getCurrentTokensUpgradeState(): UpgradeStateType {
        const upgradeElements = this.upgradeItemModel.elements.value;
        return this.upgradeItemModel.lock.value.status ? UpgradeStateType.LOCKED :
            upgradeElements.needToFix > 0 ? UpgradeStateType.FIX :
                upgradeElements.needToUpgrade === 0 ? UpgradeStateType.DONE : UpgradeStateType.AVAILABLE;
    }

    private build(): void {
        this.frame = new Sprite(this.upgradeResources.assets_views_upgrade_upgrade_panel_upgrade_panel_frame_png);
        this.addChild(this.frame);
        this.light = new Sprite(this.upgradeResources.assets_views_upgrade_upgrade_panel_light1_png);
        this.light.position.set(this.frame.width * 0.5 - this.light.width * 0.5, -this.light.height);
        this.addChild(this.light);
        this.upgradeResourceName = new Text(this.upgradeItemModel.title.value, new UpgradeResourcesNameTextStyle());
        this.addChild(this.upgradeResourceName);
        this.separetor = new Sprite(this.upgradeResources.assets_views_upgrade_upgrade_panel_separetor_png);
        this.separetor.position.set(266, 139.8);
        this.addChild(this.separetor);
        const costInCoins = this.upgradeItemModel.elements.value.needToFix > 0 ? this.upgradeItemModel.fixCostInCoins.value : this.upgradeItemModel.upgradeCostInCoins.value;
        this.upgradeCoinButton = new UpgradeCoinButton(this.upgradeResources, this.coinsUpgradeStateType, costInCoins);
        this.upgradeCoinButton.position.set(393, 165.8 + this.upgradeCoinButton.height * 0.5);
        this.upgradeCoinButton.pivot.set(this.upgradeCoinButton.width * 0.5, this.upgradeCoinButton.height * 0.5);
        this.upgradeCoinButton.onUp.add(this.upgradeCoinsButtonClickHandle, this);
        this.addChild(this.upgradeCoinButton);
        const tokensAmountFromCoins = this.upgradeItemModel.elements.value.needToFix > 0 ? this.upgradeItemModel.returnedTokensAmountForCoinsFix.value : this.upgradeItemModel.returnedTokensAmountForCoinsUpgrade.value;
        this.upgradeCoinRewardText = new GetRewardContainer(this.commonResources, Number(tokensAmountFromCoins));
        this.upgradeCoinRewardText.position.set(337.84, 278);
        this.addChild(this.upgradeCoinRewardText);
        const costInTokens = this.upgradeItemModel.elements.value.needToFix > 0 ? this.upgradeItemModel.fixCostInTokens.value : this.upgradeItemModel.upgradeCostInTokens.value;
        this.upgradeTokenButton = new UpgradeTokenButton(this.commonResources, this.upgradeResources, this.tokensUpgradeStateType, Number(costInTokens));
        this.upgradeTokenButton.position.set(626, 165.8 + this.upgradeTokenButton.height * 0.5);
        this.upgradeTokenButton.pivot.set(this.upgradeTokenButton.width * 0.5, this.upgradeTokenButton.height * 0.5);
        this.upgradeTokenButton.onUp.add(this.upgradeTokensButtonClickHandle, this);
        this.addChild(this.upgradeTokenButton);
        const tokensAmountFromToken = this.upgradeItemModel.elements.value.needToFix > 0 ? this.upgradeItemModel.returnedTokensAmountForTokensFix.value : this.upgradeItemModel.returnedTokensAmountForTokensUpgrade.value;
        this.upgradeTokenRewardText = new GetRewardContainer(this.commonResources, Number(tokensAmountFromToken));
        this.upgradeTokenRewardText.position.set(557.84, 278);
        this.addChild(this.upgradeTokenRewardText);
        this.upgradedText = new Text(UpgradePanel.UPGRADED_TEXT, new UpgradedTextStyle());
        this.upgradedText.position.set(350 + this.upgradedText.width * 0.5, 165.8 + this.upgradedText.height * 0.5);
        this.addChild(this.upgradedText);

        this.resource = new UpgradeResourceIndicator(
            this.upgradeResources,
            this.upgradeItemModel
        );
        this.resource.position.set(23.5, 76.5);
        this.addChild(this.resource);
        this.fixText = new Text(UpgradePanel.FIX_TEXT, new FixTextStyle());
        this.fixText.position.set(21, -13 - this.fixText.height);
        this.addChild(this.fixText);
        this.lockedRibbon = new Sprite(this.upgradeResources.assets_views_upgrade_upgrade_panel_locked_ribbon_png);
        this.lockedRibbon.eventMode = "static";
        this.lockedRibbon.position.y = 18.5;
        this.addChild(this.lockedRibbon);
        this.lockedText = new Text(`${UpgradePanel.UNLOCK_TEXT} ${this.upgradeItemModel.lock.value.unlockedFromBuildingLevel || 10}`, new LockedTextStyle());
        this.lockedText.position.set(125.5, 178.5);
        this.lockedText.angle = -3.5;
        this.lockedText.skew.x = -0.0087;
        this.lockedRibbon.addChild(this.lockedText);
        this.rightButton = new DefaultButton(this.upgradeResources.assets_views_upgrade_upgrade_panel_back_button_png);
        this.rightButton.width = 26.36;
        this.rightButton.height = 43.04;
        this.rightButton.position.set(517, 44.2);
        this.addChild(this.rightButton);
        this.leftButton = new DefaultButton(this.upgradeResources.assets_views_upgrade_upgrade_panel_back_button_png);
        this.leftButton.width = 26.36;
        this.leftButton.height = 43.04;
        this.leftButton.scale.x *= -1;
        this.leftButton.position.set(240, 44.2);
        this.addChild(this.leftButton);
    }

    private show(): void {
        // todo: always available
        this.tokenButtonAvailable = true;
        this.upgradeTokenButton.updateLockStatus(!this.tokenButtonAvailable);
        if (!this.tokenButtonAvailable) {
            this.upgradeTokenButton.visible = false;
            this.upgradeTokenRewardText.visible = false;
            this.upgradeCoinButton.position.x = 500;
            this.upgradeCoinRewardText.position.x = 444;
        }
        this.updateResourcePanel(this.upgradeItemModel);
    }

    private async upgradeCoinsButtonClickHandle(): Promise<void> {
        this.onUpgradeButtonClick.emit(this.upgradeItemModel, true);
        const buttonAnimation = new gsap.core.Timeline();
        buttonAnimation.fromTo(this.upgradeCoinButton.scale, { x: 1, y: 1 }, { x: 0.9, y: 0.9, repeat: 1, yoyo: true, duration: 0.1 });
        await buttonAnimation.play();
        buttonAnimation.kill();
    }

    private async upgradeTokensButtonClickHandle(): Promise<void> {
        this.onUpgradeButtonClick.emit(this.upgradeItemModel, false);
        const buttonAnimation = new gsap.core.Timeline();
        buttonAnimation.fromTo(this.upgradeTokenButton.scale, { x: 1, y: 1 }, { x: 0.9, y: 0.9, repeat: 1, yoyo: true, duration: 0.1 });
        await buttonAnimation.play();
        buttonAnimation.kill();
    }

    public async makeUpgradeOrFix(): Promise<void> {
        await this.resource.updateProgress();
    }

    public updateResourcePanel(selectedUpgradeModel: IUpgradeBuildingModel, fromUpgradeOrFix: boolean = false): void {
        this.upgradeItemModel = selectedUpgradeModel;
        this.coinsUpgradeStateType = this.getCurrentCoinsUpgradeState();
        this.tokensUpgradeStateType = this.getCurrentTokensUpgradeState();
        this.changePanelByUpgradeStateType();
        this.updateValues(fromUpgradeOrFix);
    }

    private changePanelByUpgradeStateType(): void {
        this.upgradeCoinButton.updateIconByUpgradeStateType(this.coinsUpgradeStateType);
        this.upgradeTokenButton.updateIconByUpgradeStateType(this.tokensUpgradeStateType);
        switch (this.coinsUpgradeStateType) {
            case UpgradeStateType.FIX:
                this.light.texture = this.upgradeResources.assets_views_upgrade_upgrade_panel_light3_png;
                this.frame.tint = UpgradePanel.FIX_FRAME_TINT;
                this.fixText.visible = this.resource.visible = this.upgradeCoinButton.visible = this.upgradeCoinRewardText.visible = this.separetor.visible = true;
                this.upgradeTokenRewardText.visible = this.upgradeTokenButton.visible = this.tokenButtonAvailable;
                this.lockedRibbon.visible = this.upgradedText.visible = false;
                break;
            case UpgradeStateType.LOCKED:
                this.light.texture = this.upgradeResources.assets_views_upgrade_upgrade_panel_light1_png;
                this.frame.tint = 0xFFFFFF;
                this.fixText.visible = this.resource.visible = this.upgradeCoinButton.visible = this.upgradeCoinRewardText.visible = this.separetor.visible = this.upgradedText.visible = false;
                this.upgradeTokenRewardText.visible = this.upgradeTokenButton.visible = false;
                this.lockedRibbon.visible = true;
                break;
            case UpgradeStateType.AVAILABLE:
                this.light.texture = this.upgradeResources.assets_views_upgrade_upgrade_panel_light1_png;
                this.frame.tint = 0xFFFFFF;
                this.fixText.visible = this.lockedRibbon.visible = this.upgradedText.visible = false;
                this.upgradeTokenRewardText.visible = this.upgradeTokenButton.visible = this.tokenButtonAvailable;
                this.resource.visible = this.upgradeCoinButton.visible = this.upgradeCoinRewardText.visible = this.separetor.visible = true;
                break;
            case UpgradeStateType.DONE:
                this.light.texture = this.upgradeResources.assets_views_upgrade_upgrade_panel_light2_png;
                this.frame.tint = UpgradePanel.DONE_FRAME_TINT;
                this.fixText.visible = this.lockedRibbon.visible = this.upgradeCoinButton.visible = this.upgradeCoinRewardText.visible = false;
                this.upgradeTokenRewardText.visible = this.upgradeTokenButton.visible = false;
                this.resource.visible = this.separetor.visible = this.upgradedText.visible = true;
                break;
        }
    }

    private updateValues(fromUpgradeOrFix: boolean = false): void {
        this.upgradeResourceName.scale.set(1);
        this.upgradeResourceName.text = this.upgradeItemModel.title.value;
        if (this.upgradeResourceName.width >= 250) {
            this.upgradeResourceName.scale.set(250 / this.upgradeResourceName.width);
        }
        this.upgradeResourceName.position.set(this.frame.x + this.frame.width * 0.5 - this.upgradeResourceName.width * 0.5, this.frame.y + 68 - this.upgradeResourceName.height * 0.5);
        this.lockedText.text = `${UpgradePanel.UNLOCK_TEXT} ${this.upgradeItemModel.lock.value.unlockedFromBuildingLevel || 10}`;
        this.resource.updateResource(this.upgradeItemModel, fromUpgradeOrFix);
        const costInCoins = this.upgradeItemModel.elements.value.needToFix > 0 ? this.upgradeItemModel.fixCostInCoins.value : this.upgradeItemModel.upgradeCostInCoins.value;
        this.upgradeCoinButton.updateCostValue(costInCoins);
        const costInTokens = this.upgradeItemModel.elements.value.needToFix > 0 ? this.upgradeItemModel.fixCostInTokens.value : this.upgradeItemModel.upgradeCostInTokens.value;
        this.upgradeTokenButton.updateCostValue(Number(costInTokens));
        const tokensAmountFromCoins = this.upgradeItemModel.elements.value.needToFix > 0 ? this.upgradeItemModel.returnedTokensAmountForCoinsFix.value : this.upgradeItemModel.returnedTokensAmountForCoinsUpgrade.value;
        this.upgradeCoinRewardText.setGreenRewardText(tokensAmountFromCoins);
        const tokensAmountFromToken = this.upgradeItemModel.elements.value.needToFix > 0 ? this.upgradeItemModel.returnedTokensAmountForTokensFix.value : this.upgradeItemModel.returnedTokensAmountForTokensUpgrade.value;
        this.upgradeTokenRewardText.setGreenRewardText(tokensAmountFromToken);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.upgradeCoinButton.onUp.remove(this.upgradeCoinsButtonClickHandle, this);
        this.upgradeTokenButton.onUp.remove(this.upgradeTokensButtonClickHandle, this);
        super.destroy(options);
    }
}

class UpgradeResourcesNameTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48
        }));
    }
}

class FixTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: [
                "#fec607",
                "#e85165"
            ],
            fillGradientType: 1,
            fillGradientStops: [
                0.2
            ],
            fontSize: 36,
            fontFamily: GameFont.DEFAULT_FONT
        }));
    }
}

class LockedTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: [
                "#7f6457",
                "#270607"
            ],
            fillGradientStops: [
                0.4
            ],
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 96
        }));
    }
}

class UpgradedTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: [
                "#AAFD09",
                "#16CF15"
            ],
            fillGradientStops: [
                0.15
            ],
            fontSize: 52,
            fontFamily: GameFont.DEFAULT_FONT
        }));
    }
}
